import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { RecaptchaVerifier,signInWithPhoneNumber,} from "firebase/auth";

import { firebaseAuth } from "@/lib/firebase";

const HERO = "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1400&q=80";

export default function Login() {
  const { login, loginWithFirebaseToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/profile";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Mobile OTP login state
const [mobileLogin, setMobileLogin] = useState(false);
const [mobileStep, setMobileStep] = useState("phone");
const [mobilePhone, setMobilePhone] = useState("");
const [mobileOtp, setMobileOtp] = useState("");

const [showRecaptcha, setShowRecaptcha] = useState(false);
const [captchaVerified, setCaptchaVerified] = useState(false);

const recaptchaVerifierRef = useRef(null);
const confirmationResultRef = useRef(null);

useEffect(() => {
  if (
    !mobileLogin ||
    mobileStep !== "phone" ||
    !showRecaptcha
  ) {
    return;
  }

  if (recaptchaVerifierRef.current) {
    return;
  }

  const verifier = new RecaptchaVerifier(
    firebaseAuth,
    "recaptcha-container",
    {
      size: "normal",
      callback: () => {
        setCaptchaVerified(true);
      },
      "expired-callback": () => {
        setCaptchaVerified(false);
        toast.error("reCAPTCHA expired. Please verify again.");
      },
    }
  );

  recaptchaVerifierRef.current = verifier;

  verifier.render().catch((error) => {
    console.error("reCAPTCHA render error:", error);
    recaptchaVerifierRef.current = null;
    setCaptchaVerified(false);
    toast.error("Could not load reCAPTCHA. Please try again.");
  });

  return () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  };
}, [mobileLogin, mobileStep, showRecaptcha]);

  // Forgot password state
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Normal email/password login
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await login(email, password);

      toast.success("Welcome back");
      navigate(from);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Login failed";

      if (msg.includes("verify")) {
        toast.error(msg);

        navigate("/signup", {
          state: {
            email,
            needsOtp: true,
          },
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };


// Send Firebase mobile OTP
const continueWithMobile = (e) => {
  e.preventDefault();

  if (mobilePhone.length !== 10) {
    toast.error("Enter a valid 10-digit mobile number.");
    return;
  }

  setCaptchaVerified(false);
  setShowRecaptcha(true);
};

const sendMobileOtp = async (e) => {
  e.preventDefault();

  if (mobilePhone.length !== 10) {
    toast.error("Enter a valid 10-digit mobile number.");
    return;
  }

  if (!captchaVerified) {
    toast.error("Please complete the reCAPTCHA first.");
    return;
  }

  if (!recaptchaVerifierRef.current) {
    toast.error("Please complete the reCAPTCHA first.");
    return;
  }

  setSubmitting(true);

  try {
    const phone = `+91${mobilePhone}`;

    const confirmationResult = await signInWithPhoneNumber(
      firebaseAuth,
      phone,
      recaptchaVerifierRef.current
    );

    confirmationResultRef.current = confirmationResult;

    toast.success("Verification code sent.");

    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }

    setShowRecaptcha(false);
    setCaptchaVerified(false);
    setMobileStep("otp");
  } catch (err) {
    console.error("Firebase OTP error:", err);

    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }

    setShowRecaptcha(false);
    setCaptchaVerified(false);

    toast.error(
      err?.message ||
        "Could not send verification code."
    );
  } finally {
    setSubmitting(false);
  }
};

// Verify Firebase mobile OTP
const verifyMobileOtp = async (e) => {
  e.preventDefault();

  if (mobileOtp.length !== 6) {
    toast.error("Enter the 6-digit code.");
    return;
  }

  if (!confirmationResultRef.current) {
    toast.error("Please request a new verification code.");
    return;
  }

  setSubmitting(true);

  try {
    const result = await confirmationResultRef.current.confirm(
      mobileOtp
    );

    const firebaseIdToken = await result.user.getIdToken();

    await loginWithFirebaseToken(firebaseIdToken);

    toast.success("Welcome back");
    navigate(from);
  } catch (err) {
    console.error(
      "Firebase OTP verification error:",
      err
    );

    toast.error(
      err?.response?.data?.detail ||
        err?.message ||
        "Invalid verification code."
    );
  } finally {
    setSubmitting(false);
  }
};

  // Send forgot-password OTP
  const sendForgotPasswordOtp = async (e) => {
    e.preventDefault();

    if (!forgotEmail.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post(
        "/auth/forgot-password",
        {
          email: forgotEmail.trim(),
        }
      );

      toast.success(data.message);

      if (data.dev_otp) {
        toast(
          `Dev OTP: ${data.dev_otp}`,
          {
            duration: 12000,
          }
        );
      }

      setForgotStep("otp");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not send password reset code."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Verify forgot-password OTP
  const verifyForgotPasswordOtp = async (e) => {
    e.preventDefault();

    if (forgotOtp.length !== 6) {
      toast.error("Enter the 6-digit code.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post(
        "/auth/verify-password-reset-otp",
        {
          email: forgotEmail.trim(),
          otp: forgotOtp,
        }
      );

      toast.success(data.message);

      setForgotStep("password");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Invalid verification code."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Reset password
  const resetPassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error(
        "Password must be at least 6 characters."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post(
        "/auth/reset-password",
        {
          email: forgotEmail.trim(),
          otp: forgotOtp,
          new_password: newPassword,
        }
      );

      toast.success(data.message);

      // Return to normal login
      setForgotPassword(false);
      setForgotStep("email");
      setForgotEmail("");
      setForgotOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setPassword("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not reset password."
      );
    } finally {
      setSubmitting(false);
    }
  };

return (
  <div
    className="min-h-[calc(100vh-80px)] grid md:grid-cols-2"
    data-testid="login-page"
  >
    <div className="hidden md:block relative">
      <img
        src={HERO}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />

      <div className="absolute bottom-12 left-12 text-white max-w-md">
        <div className="ie-overline opacity-80 mb-3">
          The Atelier
        </div>

        <div className="font-serif text-4xl leading-tight">
          Welcome back to the quiet edit.
        </div>
      </div>
    </div>

    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="flex items-center justify-center px-5 sm:px-6 py-12 md:py-16 md:px-20"
    >
      <div className="w-full max-w-md">

        {/* =====================================================
            FORGOT PASSWORD FLOW
        ====================================================== */}

        {forgotPassword ? (
          <>

            {/* EMAIL STEP */}
            {forgotStep === "email" && (
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setForgotPassword(false);
                    setForgotStep("email");
                  }}
                  className="text-xs text-text-secondary hover:text-brand-accent transition-colors mb-8"
                >
                  ← Back to Sign In
                </button>

                <div className="ie-overline text-brand-accent mb-4">
                  Reset password
                </div>

                <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-3">
                  A fresh beginning.
                </h1>

                <p className="text-sm text-text-secondary leading-relaxed mb-10">
                  Enter your email address and we'll send you a
                  verification code to reset your password.
                </p>

                <form
                  onSubmit={sendForgotPasswordOtp}
                  className="space-y-8"
                >
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Email
                    </label>

                    <input
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(e) =>
                        setForgotEmail(e.target.value)
                      }
                      className="ie-input"
                      autoComplete="email"
                      placeholder="Enter your email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="ie-btn-primary w-full justify-center"
                  >
                    {submitting
                      ? "Sending…"
                      : "Send Verification Code"}
                  </button>
                </form>
              </div>
            )}

            {/* OTP STEP */}
            {forgotStep === "otp" && (
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setForgotStep("email");
                  }}
                  className="text-xs text-text-secondary hover:text-brand-accent transition-colors mb-8"
                >
                  ← Change email
                </button>

                <div className="ie-overline text-brand-accent mb-4">
                  Verify identity
                </div>

                <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-3">
                  A code is on its way.
                </h1>

                <p className="text-sm text-text-secondary leading-relaxed mb-10">
                  We've sent a 6-digit verification code to{" "}
                  <span className="font-serif text-foreground">
                    {forgotEmail}
                  </span>
                  .
                </p>

                <form
                  onSubmit={verifyForgotPasswordOtp}
                  className="space-y-8"
                >
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Verification Code
                    </label>

                    <input
                      type="text"
                      required
                      value={forgotOtp}
                      onChange={(e) =>
                        setForgotOtp(
                          e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6)
                        )
                      }
                      className="ie-input tracking-[0.4em]"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      autoComplete="one-time-code"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={
                      submitting ||
                      forgotOtp.length !== 6
                    }
                    className="ie-btn-primary w-full justify-center"
                  >
                    {submitting
                      ? "Verifying…"
                      : "Verify Code"}
                  </button>
                </form>
              </div>
            )}

            {/* NEW PASSWORD STEP */}
            {forgotStep === "password" && (
              <div>
                <div className="ie-overline text-brand-accent mb-4">
                  New password
                </div>

                <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-3">
                  Create a new password.
                </h1>

                <p className="text-sm text-text-secondary leading-relaxed mb-10">
                  Choose a new password for your INDUS EIRENE account.
                </p>

                <form
                  onSubmit={resetPassword}
                  className="space-y-8"
                >
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      New Password
                    </label>

                    <input
                      type="password"
                      required
                      minLength={6}
                      value={newPassword}
                      onChange={(e) =>
                        setNewPassword(e.target.value)
                      }
                      className="ie-input"
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Confirm Password
                    </label>

                    <input
                      type="password"
                      required
                      minLength={6}
                      value={confirmPassword}
                      onChange={(e) =>
                        setConfirmPassword(e.target.value)
                      }
                      className="ie-input"
                      autoComplete="new-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="ie-btn-primary w-full justify-center"
                  >
                    {submitting
                      ? "Updating…"
                      : "Set New Password"}
                  </button>
                </form>
              </div>
            )}

          </>
        ) : (

          <>
            <div className="ie-overline text-brand-accent mb-4">
              Sign in
            </div>

            <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-3">
              Your private atelier.
            </h1>

            <p className="text-sm text-text-secondary leading-relaxed mb-10">
              Sign in to access your wishlist, orders, and personal editions.
            </p>

            {/* =====================================================
                LOGIN METHOD SWITCH
            ====================================================== */}

            <div className="flex border-b border-border mb-8">

              {/* EMAIL LOGIN */}
              <button
                type="button"
                onClick={() => {
                  if (recaptchaVerifierRef.current) {
                    recaptchaVerifierRef.current.clear();
                    recaptchaVerifierRef.current = null;
                  }

                  setMobileLogin(false);
                  setMobileStep("phone");
                  setShowRecaptcha(false);
                  setCaptchaVerified(false);
                }}
                className={`flex-1 pb-3 text-xs transition-colors ${
                  !mobileLogin
                    ? "text-foreground border-b-2 border-foreground"
                    : "text-text-secondary hover:text-foreground"
                }`}
              >
                Email & Password
              </button>

              {/* MOBILE LOGIN */}
              <button
                type="button"
                onClick={() => {
                  setMobileLogin(true);
                  setMobileStep("phone");
                  setShowRecaptcha(false);
                  setCaptchaVerified(false);
                }}
                className={`flex-1 pb-3 text-xs transition-colors ${
                  mobileLogin
                    ? "text-foreground border-b-2 border-foreground"
                    : "text-text-secondary hover:text-foreground"
                }`}
              >
                Mobile OTP
              </button>

            </div>

            {/* =====================================================
                EMAIL + PASSWORD LOGIN
            ====================================================== */}

            {!mobileLogin ? (
              <>
                <form
                  onSubmit={submit}
                  className="space-y-8"
                  data-testid="login-form"
                >
                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Email
                    </label>

                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) =>
                        setEmail(e.target.value)
                      }
                      className="ie-input"
                      data-testid="login-email"
                      autoComplete="email"
                    />
                  </div>

                  <div>
                    <label className="ie-overline text-text-secondary block mb-2">
                      Password
                    </label>

                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) =>
                        setPassword(e.target.value)
                      }
                      className="ie-input"
                      data-testid="login-password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="flex justify-end -mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotPassword(true);
                        setForgotEmail(email);
                      }}
                      className="text-xs text-text-secondary hover:text-brand-accent transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="ie-btn-primary w-full justify-center"
                    data-testid="login-submit"
                  >
                    {submitting
                      ? "Signing in…"
                      : "Sign In"}
                  </button>
                </form>
              </>

            ) : (

              <>
                {/* =====================================================
                    MOBILE OTP LOGIN
                ====================================================== */}

                {mobileStep === "phone" ? (

                  <>
                    {!showRecaptcha ? (

                      /* -------------------------------
                         MOBILE NUMBER STEP
                      -------------------------------- */

                      <form
                        onSubmit={continueWithMobile}
                        className="space-y-8"
                      >
                        <div>
                          <label className="ie-overline text-text-secondary block mb-2">
                            Mobile Number
                          </label>

                          <div className="flex">

                            <div className="flex items-center px-4 border border-r-0 border-border bg-muted/30 text-sm text-text-secondary rounded-l-md">
                              +91
                            </div>

                            <input
                              type="tel"
                              required
                              value={mobilePhone}
                              onChange={(e) =>
                                setMobilePhone(
                                  e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 10)
                                )
                              }
                              className="ie-input rounded-l-none flex-1"
                              inputMode="numeric"
                              autoComplete="tel-national"
                              placeholder="9876543210"
                              maxLength={10}
                            />

                          </div>

                          <p className="text-xs text-text-secondary mt-2">
                            Enter your 10-digit mobile number.
                          </p>
                        </div>

                        <button
                          type="submit"
                          disabled={mobilePhone.length !== 10}
                          className="ie-btn-primary w-full justify-center"
                        >
                          Continue
                        </button>

                      </form>

                    ) : (

                      /* -------------------------------
                         CAPTCHA STEP
                      -------------------------------- */

                      <form
                        onSubmit={sendMobileOtp}
                        className="space-y-8"
                      >

                        <div>

                          <button
                            type="button"
                            onClick={() => {
                              if (recaptchaVerifierRef.current) {
                                recaptchaVerifierRef.current.clear();
                                recaptchaVerifierRef.current = null;
                              }

                              setShowRecaptcha(false);
                              setCaptchaVerified(false);
                            }}
                            className="text-xs text-text-secondary hover:text-brand-accent transition-colors mb-8"
                          >
                            ← Change mobile number
                          </button>

                          <div className="ie-overline text-brand-accent mb-4">
                            Verify you're human
                          </div>

                          <h2 className="font-serif text-3xl leading-tight tracking-tight mb-3">
                            One quick check.
                          </h2>

                          <p className="text-sm text-text-secondary leading-relaxed mb-6">
                            Please complete the security check before we send your verification code.
                          </p>

                          <div
                            id="recaptcha-container"
                            className="min-h-[78px]"
                          />

                        </div>

                        <button
                          type="submit"
                          disabled={
                            !captchaVerified ||
                            submitting
                          }
                          className="ie-btn-primary w-full justify-center"
                        >
                          {submitting
                            ? "Sending…"
                            : "Send Verification Code"}
                        </button>

                      </form>

                    )}
                  </>

                ) : (

                  /* =================================================
                     OTP VERIFICATION STEP
                  ================================================== */

                  <form
                    onSubmit={verifyMobileOtp}
                    className="space-y-8"
                  >

                    <button
                      type="button"
                      onClick={() => {
                        setMobileStep("phone");
                        setMobileOtp("");
                        setShowRecaptcha(false);
                        setCaptchaVerified(false);
                        confirmationResultRef.current = null;
                      }}
                      className="text-xs text-text-secondary hover:text-brand-accent transition-colors"
                    >
                      ← Change mobile number
                    </button>

                    <div>
                      <div className="ie-overline text-brand-accent mb-4">
                        Verify identity
                      </div>

                      <h2 className="font-serif text-3xl leading-tight tracking-tight mb-3">
                        A code is on its way.
                      </h2>

                      <p className="text-sm text-text-secondary leading-relaxed">
                        We've sent a 6-digit verification code to{" "}
                        <span className="font-serif text-foreground">
                          +91 {mobilePhone}
                        </span>
                        .
                      </p>
                    </div>

                    <div>
                      <label className="ie-overline text-text-secondary block mb-2">
                        Verification Code
                      </label>

                      <input
                        type="text"
                        required
                        value={mobileOtp}
                        onChange={(e) =>
                          setMobileOtp(
                            e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6)
                          )
                        }
                        className="ie-input tracking-[0.4em]"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        autoComplete="one-time-code"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={
                        submitting ||
                        mobileOtp.length !== 6
                      }
                      className="ie-btn-primary w-full justify-center"
                    >
                      {submitting
                        ? "Verifying…"
                        : "Verify & Sign In"}
                    </button>

                  </form>

                )}

              </>
            )}

            {/* =====================================================
                CREATE ACCOUNT
            ====================================================== */}

            <div className="mt-10 pt-6 border-t border-border text-sm text-text-secondary">
              New to the house?{" "}

              <Link
                to="/signup"
                className="text-foreground hover:text-brand-accent ie-overline ml-1"
                data-testid="login-go-signup"
              >
                Create Account
              </Link>
            </div>

          </>

        )}

      </div>
    </motion.div>
  </div>
);

}